<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useSeoHead } from "@/composables/use-seo-head";
import { DEFAULT_LOCALE, pathFor, type Locale } from "@/seo/routes-manifest";

const { t } = useI18n();
const route = useRoute();

useSeoHead("philosophy");

const homePath = computed(() =>
  pathFor("home", (route.meta?.locale as Locale | undefined) ?? DEFAULT_LOCALE),
);

const principleKeys = [
  { key: "preparedEnv", icon: "i-lucide-eye" },
  { key: "progressGrows", icon: "i-lucide-shield-check" },
  { key: "trustOverControl", icon: "i-lucide-hand-heart" },
  { key: "worldGrows", icon: "i-lucide-trees" },
] as const;

const fiveSKeys = ["seiri", "seiton", "seiso", "seiketsu", "shitsuke"] as const;
</script>

<template>
  <article class="philosophy-page">
    <!-- Back link -->
    <div class="px-5 pt-8 md:px-12">
      <div class="mx-auto max-w-[var(--max-w-content)]">
        <router-link
          :to="homePath"
          class="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary)] transition-colors hover:underline"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
          {{ t("philosophyPage.backToHome") }}
        </router-link>
      </div>
    </div>

    <!-- Hero -->
    <section class="px-5 pt-10 pb-12 md:px-12 md:pt-16 md:pb-24">
      <div class="mx-auto max-w-[var(--max-w-content)] text-center">
        <h1
          data-reveal
          class="fade-up text-[32px] leading-[1.15] font-medium tracking-[-0.02em] text-[var(--color-text-primary)] md:text-[48px]"
          style="font-family: var(--font-display)"
        >
          {{ t("philosophyPage.heading") }}
        </h1>
        <p
          data-reveal
          data-reveal-delay="1"
          class="fade-up mx-auto mt-5 max-w-[560px] text-base leading-[1.7] text-[var(--color-text-secondary)] md:text-lg"
        >
          {{ t("philosophyPage.intro") }}
        </p>
      </div>
    </section>

    <!-- Montessori -->
    <section class="bg-[var(--color-surface)] px-5 py-12 md:px-12 md:py-24">
      <div class="mx-auto max-w-[var(--max-w-content)]">
        <p
          data-reveal
          class="fade-up text-sm font-medium tracking-wide text-[var(--color-primary)] uppercase"
        >
          {{ t("philosophyPage.firstPillar") }}
        </p>
        <h2
          data-reveal
          data-reveal-delay="1"
          class="fade-up mt-3 text-2xl font-medium text-[var(--color-text-primary)] md:text-[36px] md:leading-[1.15]"
          style="font-family: var(--font-display)"
        >
          {{ t("philosophyPage.montessori.title") }}
        </h2>

        <blockquote
          data-reveal
          data-reveal-delay="2"
          class="fade-up mt-6 border-l-2 border-[var(--color-primary)] py-1 pl-5"
        >
          <p
            class="text-lg leading-[1.6] font-medium text-[var(--color-text-primary)] italic md:text-xl"
            style="font-family: var(--font-display)"
          >
            {{ t("philosophyPage.montessori.quote") }}
          </p>
        </blockquote>

        <div
          data-reveal
          data-reveal-delay="3"
          class="fade-up mt-6 space-y-4 text-base leading-[1.7] text-[var(--color-text-secondary)] md:text-lg"
        >
          <p>{{ t("philosophyPage.montessori.p1") }}</p>
          <p>{{ t("philosophyPage.montessori.p2") }}</p>
          <p>{{ t("philosophyPage.montessori.p3") }}</p>
        </div>
      </div>
    </section>

    <!-- Shitsuke -->
    <section class="bg-[var(--color-bg-secondary)] px-5 py-12 md:px-12 md:py-24">
      <div class="mx-auto max-w-[var(--max-w-content)]">
        <p
          data-reveal
          class="fade-up text-sm font-medium tracking-wide text-[var(--color-primary)] uppercase"
        >
          {{ t("philosophyPage.secondPillar") }}
        </p>
        <h2
          data-reveal
          data-reveal-delay="1"
          class="fade-up mt-3 text-2xl font-medium text-[var(--color-text-primary)] md:text-[36px] md:leading-[1.15]"
          style="font-family: var(--font-display)"
        >
          {{ t("philosophyPage.shitsuke.title") }}
          <span class="ml-1 text-[var(--color-text-muted)]">{{
            t("philosophyPage.shitsuke.kanji")
          }}</span>
        </h2>

        <div
          data-reveal
          data-reveal-delay="2"
          class="fade-up mt-6 space-y-4 text-base leading-[1.7] text-[var(--color-text-secondary)] md:text-lg"
        >
          <p>{{ t("philosophyPage.shitsuke.p1") }}</p>
          <p>{{ t("philosophyPage.shitsuke.p2") }}</p>
          <p>{{ t("philosophyPage.shitsuke.p3") }}</p>
        </div>

        <div
          data-reveal
          data-reveal-delay="3"
          class="fade-up mt-8 rounded-xl bg-[var(--color-surface)] p-6 md:p-8"
          style="box-shadow: var(--shadow-card)"
        >
          <p class="text-sm font-medium tracking-wide text-[var(--color-text-muted)] uppercase">
            {{ t("philosophyPage.fiveS.heading") }}
          </p>
          <ol class="mt-4 space-y-2.5 text-base text-[var(--color-text-secondary)]">
            <li v-for="(sKey, i) in fiveSKeys" :key="sKey" class="flex items-start gap-3">
              <span class="mt-0.5 text-sm text-[var(--color-text-muted)]">{{ i + 1 }}.</span>
              <span
                ><strong class="font-medium text-[var(--color-text-primary)]">{{
                  t(`philosophyPage.fiveS.${sKey}.name`)
                }}</strong>
                — {{ t(`philosophyPage.fiveS.${sKey}.description`) }}</span
              >
            </li>
          </ol>
        </div>
      </div>
    </section>

    <!-- How Cresci applies this -->
    <section class="bg-[var(--color-bg-primary)] px-5 py-12 md:px-12 md:py-24">
      <div class="mx-auto max-w-[var(--max-w-content)]">
        <h2
          data-reveal
          class="fade-up text-center text-2xl font-medium text-[var(--color-text-primary)] md:text-[36px] md:leading-[1.15]"
          style="font-family: var(--font-display)"
        >
          {{ t("philosophyPage.howApplied.heading") }}
        </h2>
        <p
          data-reveal
          data-reveal-delay="1"
          class="fade-up mx-auto mt-4 max-w-[560px] text-center text-base leading-[1.7] text-[var(--color-text-secondary)] md:text-lg"
        >
          {{ t("philosophyPage.howApplied.intro") }}
        </p>

        <div class="mt-10 grid gap-6 md:grid-cols-2">
          <div
            v-for="(principle, index) in principleKeys"
            :key="principle.key"
            data-reveal
            :data-reveal-delay="index + 1"
            class="fade-up rounded-xl bg-[var(--color-surface)] p-6"
            style="box-shadow: var(--shadow-card)"
          >
            <div
              class="flex size-10 items-center justify-center rounded-lg bg-[var(--color-primary-light)]"
            >
              <UIcon :name="principle.icon" class="size-5 text-[var(--color-primary)]" />
            </div>
            <h3
              class="mt-4 text-base font-medium text-[var(--color-text-primary)] md:text-lg"
              style="font-family: var(--font-display)"
            >
              {{ t(`philosophyPage.principles.${principle.key}.title`) }}
            </h3>
            <p class="mt-2 text-sm leading-[1.7] text-[var(--color-text-secondary)]">
              {{ t(`philosophyPage.principles.${principle.key}.description`) }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Closing -->
    <section class="bg-[var(--color-surface)] px-5 py-12 md:px-12 md:py-24">
      <div class="mx-auto max-w-[var(--max-w-content)] text-center">
        <p
          data-reveal
          class="fade-up mx-auto max-w-[520px] text-base leading-[1.7] text-[var(--color-text-secondary)] md:text-lg"
        >
          {{ t("philosophyPage.closing") }}
        </p>
        <router-link
          data-reveal
          data-reveal-delay="1"
          :to="homePath"
          class="fade-up mt-8 inline-flex items-center gap-2 text-[15px] font-medium text-[var(--color-primary)] transition-colors hover:underline"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
          {{ t("philosophyPage.backToHome") }}
        </router-link>
      </div>
    </section>
  </article>
</template>

<style scoped>
.philosophy-page blockquote {
  border-color: var(--color-primary);
}
</style>
